import { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markAllRead, deleteNotification, deleteAllNotifications } from '../services/patientService';

export function usePatientNotifications() {
  const { socket }                    = useSocket();
  const { isLoggedIn, role }          = useAuth();
  const isPatient                     = isLoggedIn && role === 'patient';
  const [notifications, setNotifs]    = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading]         = useState(false);

  const load = useCallback(async () => {
    if (!isPatient) return;
    setLoading(true);
    try {
      const res  = await getNotifications({ limit: 50 });
      const list = res.notifications || [];
      setNotifs(list);
      setUnreadCount(list.filter(n => !n.isRead).length);
    } catch { /* silent */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatient]);

  useEffect(() => { if (isPatient) load(); }, [load, isPatient]);

  // Real-time socket updates
  useEffect(() => {
    if (!socket) return;
    const onNew   = (notif) => { setNotifs(p => [notif, ...p].slice(0, 50)); setUnreadCount(p => p + 1); };
    const onCount = ({ count }) => setUnreadCount(count);
    socket.on('new-notification',   onNew);
    socket.on('notification-count', onCount);
    return () => {
      socket.off('new-notification',   onNew);
      socket.off('notification-count', onCount);
    };
  }, [socket]);

  const markRead = useCallback(async () => {
    try {
      await markAllRead();
      setNotifs(p => p.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  }, []);

  const deleteOne = useCallback(async (id) => {
    try {
      await deleteNotification(id);
      setNotifs(p => {
        const updated = p.filter(n => n._id !== id);
        setUnreadCount(updated.filter(n => !n.isRead).length);
        return updated;
      });
    } catch { /* silent */ }
  }, []);

  const deleteAll = useCallback(async () => {
    try {
      await deleteAllNotifications();
      setNotifs([]);
      setUnreadCount(0);
    } catch { /* silent */ }
  }, []);

  return { notifications, unreadCount, loading, load, markRead, deleteOne, deleteAll };
}
